import { useCallback, useState } from 'react';
import { type DragEndEvent, type DragStartEvent } from '@dnd-kit/core';
import { KeyboardSensor, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { useToggle } from '@direct-frontend/stdlib';
// eslint-disable-next-line @direct-frontend/modules/no-restricted-entity-imports -- Form тут норм
import { type MediaV2Field } from '#shared/lib/form';

export const useProductDraggableGalleryMediaField = ({
    media,
    removeMedia,
    setMedia,
}: {
    media: MediaV2Field[];
    removeMedia: (index: number) => void;
    setMedia: (media: MediaV2Field[]) => void;
}) => {
    const [draggableId, setDraggableId] = useState<string | null>(null);
    const { value: isViewerOpen, toggleOff: closeViewer, toggleOn: openViewer } = useToggle(false);
    const [index, setIndex] = useState<number>(0);

    const changeIndex = useCallback((newIndex: number) => setIndex(newIndex % media.length), [media.length]);
    const handleDeleteImage = useCallback((index: number) => removeMedia(index), [removeMedia]);
    const handleOpenImage = useCallback(
        (openIndex: number) => {
            changeIndex(openIndex);
            openViewer();
        },
        [changeIndex, openViewer]
    );

    const handleDragStart = useCallback(
        ({ active }: DragStartEvent) => {
            setDraggableId(String(active.id));
        },
        [setDraggableId]
    );

    const handleDragEnd = useCallback(
        (event: DragEndEvent) => {
            const { active, over } = event;

            if (active.id !== over?.id) {
                const { oldIndex, newIndex } = media.reduce(
                    (result, { id }, index) => {
                        if (id === active.id) {
                            result.oldIndex = index;
                        }

                        if (id === over?.id) {
                            result.newIndex = index;
                        }

                        return result;
                    },
                    {
                        oldIndex: 0,
                        newIndex: 0,
                    }
                );

                const sorted = arrayMove(media, oldIndex, newIndex).map((props, index) => {
                    return {
                        ...props,
                        display_sequence: index,
                    };
                });

                setMedia(sorted);
            }

            setTimeout(() => setDraggableId(null), 0);
        },
        [setDraggableId, setMedia, media]
    );

    const draggableItem = draggableId ? media.find(({ id }) => id === draggableId) : null;
    const isMultipleMedia = media.length > 1;

    const sensors = useSensors(
        useSensor(MouseSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 200,
                tolerance: 6,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    return {
        isMultipleMedia,
        sensors,
        draggableId,
        draggableItem,
        handleDragEnd,
        handleDragStart,
        handleOpenImage,
        handleDeleteImage,
        changeIndex,
        closeViewer,
        isViewerOpen,
        index,
    };
};
